import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AGENT_MEDIA_LIMITS,
  PRODUCT_GALLERY_MAX_FILES,
  PRODUCT_MEDIA_DESCRIPTION_MAX,
  PRODUCT_MEDIA_FILE_KIND,
  type ProductMediaKind,
  type ProductMediaSlotEntry,
} from "@school-sales-ai/shared";
import { closestCenter, DndContext, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  useAddProductMediaFile,
  useProductMedia,
  useRemoveProductMediaFile,
  useReorderProductGallery,
  useSetProductMediaDescription,
} from "@/hooks/useProductMedia";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { checkProductSlotFile, formatFileSize } from "@/lib/agent-media-file";
import { useCatalogDragSensors } from "./drag-sensors";
import { useBlockStrayFileDrops, useFileDropZone } from "./useFileDropZone";

/**
 * A product's media: gallery, video and pdf (docs/plans/product-media.md).
 *
 * Every action saves immediately on the media endpoints, like every other
 * catalog edit: a 16 MB video is uploaded once, the moment it is chosen.
 *
 * Each section is also a drop zone for files from the computer — one zone per
 * slot, so the admin sees exactly where a file goes. Dropping takes the same
 * path as the section's button.
 */
export function ProductMediaDialog({
  productId,
  productName,
  onClose,
}: {
  productId: string;
  productName: string;
  onClose: () => void;
}): React.JSX.Element {
  const { data, isLoading, isError } = useProductMedia(productId);
  useBlockStrayFileDrops();

  return (
    <Dialog open onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col">
        <DialogHeader>
          <DialogTitle>Medios — {productName}</DialogTitle>
        </DialogHeader>
        <p className="text-[12px] text-ink-500">
          Cada cambio se guarda al instante. El agente verá los cambios en menos de 5 minutos.
        </p>

        <div className="flex flex-col gap-4 overflow-y-auto pr-1">
          {isLoading && <p className="text-[12.5px] text-ink-500">Cargando medios…</p>}
          {isError && (
            <p className="text-[12.5px] text-red-600">No se pudieron cargar los medios.</p>
          )}
          {data && (
            <>
              <GallerySection productId={productId} slot={data.slots.gallery} />
              <SingleFileSection
                productId={productId}
                kind="video"
                title="Video"
                slot={data.slots.video}
              />
              <SingleFileSection
                productId={productId}
                kind="pdf"
                title="PDF"
                slot={data.slots.pdf}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const sectionClass = "relative flex flex-col gap-2 rounded-lg border border-ink-100 p-3";

/** Covers a section while files are dragged over it, saying what the drop will do. */
function DropOverlay({ show, label }: { show: boolean; label: string }): React.JSX.Element | null {
  if (!show) return null;
  return (
    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg border-2 border-dashed border-moss bg-white/90 px-4 text-center text-[13px] font-semibold text-moss">
      {label}
    </div>
  );
}
const smallButton =
  "rounded-lg border border-ink-200 px-2.5 py-1 text-[12px] font-medium text-ink-700 hover:bg-ink-050 disabled:opacity-40";

function GallerySection({
  productId,
  slot,
}: {
  productId: string;
  slot: ProductMediaSlotEntry | null;
}): React.JSX.Element {
  const add = useAddProductMediaFile(productId);
  const remove = useRemoveProductMediaFile(productId);
  const reorder = useReorderProductGallery(productId);
  const inputRef = useRef<HTMLInputElement>(null);
  const isDesktop = useIsDesktop();
  const sensors = useCatalogDragSensors();
  // The order just asked for, shown until the server answers — without it a
  // dropped photo would jump back to where it was for the length of a request.
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);
  const saved = slot?.files ?? [];
  const byId = new Map(saved.map((f) => [f.id, f]));
  const files =
    pendingOrder && pendingOrder.length === saved.length
      ? pendingOrder.flatMap((id) => {
          const file = byId.get(id);
          return file ? [file] : [];
        })
      : saved;
  const remaining = PRODUCT_GALLERY_MAX_FILES - files.length;
  const busy = add.isPending || remove.isPending || reorder.isPending;

  // Several photos at once, uploaded one after another so the order they were
  // picked in is the order they land in; anything past the cap is refused here
  // with the reason, not silently dropped.
  async function handlePick(picked: FileList | null): Promise<void> {
    const list = Array.from(picked ?? []);
    if (inputRef.current) inputRef.current.value = "";
    if (list.length > remaining) {
      toast.error(
        `La galería admite ${PRODUCT_GALLERY_MAX_FILES} fotos; solo caben ${remaining} más.`,
      );
    }
    for (const file of list.slice(0, Math.max(0, remaining))) {
      const error = checkProductSlotFile(file, "gallery");
      if (error) {
        toast.error(`${file.name}: ${error}`);
        continue;
      }
      try {
        await add.mutateAsync({ kind: "gallery", file });
      } catch {
        // The hook already toasted the server's reason; keep going with the rest.
      }
    }
  }

  const full = remaining <= 0;
  const drop = useFileDropZone({
    onFiles: (dropped) => void handlePick(dropped),
    disabled: full || busy,
  });

  function saveOrder(ids: string[]): void {
    setPendingOrder(ids);
    reorder.mutate(ids, { onSettled: () => setPendingOrder(null) });
  }

  function move(index: number, delta: number): void {
    const target = index + delta;
    if (target < 0 || target >= files.length) return;
    const ids = files.map((f) => f.id);
    const [moved] = ids.splice(index, 1);
    if (!moved) return;
    ids.splice(target, 0, moved);
    saveOrder(ids);
  }

  function handleDragEnd({ active, over }: DragEndEvent): void {
    if (!over || active.id === over.id) return;
    const ids = files.map((f) => f.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    saveOrder(arrayMove(ids, from, to));
  }

  return (
    <section className={sectionClass} aria-label="Galería" {...drop.zoneProps}>
      <DropOverlay
        show={drop.isOver}
        label={full ? "La galería ya tiene 10 fotos." : "Suelta las fotos para agregarlas a la galería"}
      />
      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-semibold text-ink-900">
          🖼️ Galería{" "}
          <span className="font-normal tabular-nums text-ink-500">
            {files.length}/{PRODUCT_GALLERY_MAX_FILES}
          </span>
        </h3>
        <label className={`${smallButton} cursor-pointer ${remaining <= 0 || busy ? "pointer-events-none opacity-40" : ""}`}>
          Agregar fotos
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={AGENT_MEDIA_LIMITS.image.accept}
            className="sr-only"
            aria-label="Agregar fotos a la galería"
            disabled={remaining <= 0 || busy}
            onChange={(e) => void handlePick(e.target.files)}
          />
        </label>
      </div>
      <p className="text-[11.5px] text-ink-400">
        JPG o PNG, máx. {AGENT_MEDIA_LIMITS.image.maxSizeMb} MB cada una. La primera es la portada;
        el agente manda siempre la galería completa, en este orden.
        {isDesktop && " También puedes arrastrarlas aquí desde tu computadora."}
      </p>

      {/* The same rule as the catalog: a desktop drags (the photo itself is the
          handle, so only the ✕ stays under it); a phone keeps the arrows. */}
      {files.length > 0 && isDesktop && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={files.map((f) => f.id)} strategy={rectSortingStrategy}>
            <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {files.map((file, index) => (
                <SortablePhoto
                  key={file.id}
                  id={file.id}
                  url={file.url}
                  fileName={file.fileName}
                  position={index + 1}
                  disabled={busy}
                  onRemove={() => remove.mutate(file.id)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {files.length > 0 && !isDesktop && (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {files.map((file, index) => (
            <li key={file.id} className="relative flex flex-col gap-1">
              <img
                src={file.url}
                alt={file.fileName}
                className="aspect-square w-full rounded-md border border-ink-100 object-cover"
              />
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  className="rounded p-0.5 text-ink-500 hover:bg-ink-050 disabled:opacity-30"
                  aria-label={`Mover foto ${index + 1} antes`}
                  disabled={index === 0 || busy}
                  onClick={() => move(index, -1)}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="rounded p-0.5 text-red-500 hover:bg-red-50 disabled:opacity-30"
                  aria-label={`Quitar foto ${index + 1}`}
                  disabled={busy}
                  onClick={() => remove.mutate(file.id)}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="rounded p-0.5 text-ink-500 hover:bg-ink-050 disabled:opacity-30"
                  aria-label={`Mover foto ${index + 1} después`}
                  disabled={index === files.length - 1 || busy}
                  onClick={() => move(index, 1)}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <SlotDescription productId={productId} kind="gallery" slot={slot} />
    </section>
  );
}

/**
 * A gallery photo on a desktop: the image is the drag handle (pointer after a
 * few pixels, or keyboard — Space, arrows, Space), and the ✕ under it removes.
 */
function SortablePhoto({
  id,
  url,
  fileName,
  position,
  disabled,
  onRemove,
}: {
  id: string;
  url: string;
  fileName: string;
  position: number;
  disabled: boolean;
  onRemove: () => void;
}): React.JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  // dnd-kit positions the photo by transform — the KanbanCard precedent for
  // the one inline style this codebase allows.
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, transition }
    : undefined;
  return (
    <li ref={setNodeRef} style={style} className={`relative flex flex-col gap-1 ${isDragging ? "z-10 opacity-60" : ""}`}>
      <div
        {...attributes}
        {...listeners}
        aria-label={`Arrastrar foto ${position}`}
        className="cursor-grab touch-none rounded-md active:cursor-grabbing"
      >
        <img
          src={url}
          alt={fileName}
          draggable={false}
          className="aspect-square w-full rounded-md border border-ink-100 object-cover"
        />
      </div>
      <div className="flex justify-center">
        <button
          type="button"
          className="rounded p-0.5 text-red-500 hover:bg-red-50 disabled:opacity-30"
          aria-label={`Quitar foto ${position}`}
          disabled={disabled}
          onClick={onRemove}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
}

function SingleFileSection({
  productId,
  kind,
  title,
  slot,
}: {
  productId: string;
  kind: "video" | "pdf";
  title: string;
  slot: ProductMediaSlotEntry | null;
}): React.JSX.Element {
  const add = useAddProductMediaFile(productId);
  const remove = useRemoveProductMediaFile(productId);
  const inputRef = useRef<HTMLInputElement>(null);
  const file = slot?.files[0] ?? null;
  const limits = AGENT_MEDIA_LIMITS[PRODUCT_MEDIA_FILE_KIND[kind]];
  const busy = add.isPending || remove.isPending;
  const icon = kind === "video" ? "🎬" : "📄";
  const isDesktop = useIsDesktop();
  const drop = useFileDropZone({
    onFiles: (dropped) => {
      // One slot, one file: take the first and say so, rather than guess.
      if (dropped.length > 1) {
        toast.error(`Aquí va un solo archivo; se tomó «${dropped[0]?.name ?? ""}».`);
      }
      handlePick(dropped);
    },
    disabled: busy,
  });

  function handlePick(picked: FileList | null): void {
    const chosen = picked?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!chosen) return;
    const error = checkProductSlotFile(chosen, kind);
    if (error) {
      toast.error(error);
      return;
    }
    add.mutate({ kind, file: chosen });
  }

  return (
    <section className={sectionClass} aria-label={title} {...drop.zoneProps}>
      <DropOverlay
        show={drop.isOver}
        label={`Suelta el archivo para ${file ? "reemplazar" : "subir"} ${kind === "video" ? "el video" : "el PDF"}`}
      />
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold text-ink-900">
          {icon} {title}
        </h3>
        <div className="flex items-center gap-2">
          {file && (
            <a href={file.url} target="_blank" rel="noopener noreferrer" className={smallButton}>
              Ver
            </a>
          )}
          <label className={`${smallButton} cursor-pointer ${busy ? "pointer-events-none opacity-40" : ""}`}>
            {add.isPending ? "Subiendo…" : file ? "Reemplazar" : `Subir ${title.toLowerCase()}`}
            <input
              ref={inputRef}
              type="file"
              accept={limits.accept}
              className="sr-only"
              aria-label={file ? `Reemplazar ${title.toLowerCase()}` : `Subir ${title.toLowerCase()}`}
              disabled={busy}
              onChange={(e) => handlePick(e.target.files)}
            />
          </label>
          {file && (
            <button
              type="button"
              className={`${smallButton} border-red-200 text-red-600 hover:bg-red-50`}
              disabled={busy}
              onClick={() => remove.mutate(file.id)}
            >
              Quitar
            </button>
          )}
        </div>
      </div>
      <p className="text-[11.5px] text-ink-400">
        {file
          ? `${file.fileName} · ${formatFileSize(file.fileSizeBytes)}`
          : kind === "video"
            ? `MP4 (H.264), máx. ${limits.maxSizeMb} MB.${isDesktop ? " También puedes arrastrarlo aquí." : ""}`
            : `PDF, máx. ${limits.maxSizeMb} MB.${isDesktop ? " También puedes arrastrarlo aquí." : ""}`}
      </p>
      <SlotDescription productId={productId} kind={kind} slot={slot} />
    </section>
  );
}

const DESCRIPTION_LABEL: Record<ProductMediaKind, string> = {
  gallery: "Descripción de la galería",
  video: "Descripción del video",
  pdf: "Descripción del PDF",
};

/**
 * Optional, saved on blur. It is what the agent uses to ANNOUNCE the file and
 * to offer it unprompted — not to pick it; the slot's kind already does that.
 * A slot only exists once it has a file, so the field waits for one.
 */
function SlotDescription({
  productId,
  kind,
  slot,
}: {
  productId: string;
  kind: ProductMediaKind;
  slot: ProductMediaSlotEntry | null;
}): React.JSX.Element {
  const save = useSetProductMediaDescription(productId);
  const saved = slot?.description ?? "";
  const [value, setValue] = useState(saved);

  useEffect(() => {
    setValue(saved);
  }, [saved]);

  function handleBlur(): void {
    const next = value.trim();
    if (!slot || next === saved) return;
    save.mutate({ kind, description: next });
  }

  return (
    <textarea
      value={value}
      maxLength={PRODUCT_MEDIA_DESCRIPTION_MAX}
      rows={2}
      disabled={!slot}
      aria-label={DESCRIPTION_LABEL[kind]}
      placeholder={
        slot
          ? "Qué muestra (opcional). Ej.: recorrido por el taller de costura"
          : "Sube un archivo para agregar una descripción"
      }
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleBlur}
      className="resize-none rounded-[10px] border border-ink-200 px-3 py-2 text-[12.5px] text-ink-800 focus:border-moss focus:outline-none disabled:bg-ink-050"
    />
  );
}
